import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { logout, logoutSuccess, logoutFailure, loginSuccess } from '../state/auth/auth.actions';
import { resetState } from '../state/global/global.actions';
import { EchoService } from './echo.service';
import { AppTourService } from './app-tour.service';
import { PopupService } from './popup.service';
import { AuthService } from './auth.service';

interface CrossTabMessage {
  type: 'SIGN_OUT' | 'SIGN_IN';
  userId?: number;
}

@Injectable({
  providedIn: 'root'
})
export class SessionService implements OnDestroy {

  private isSigningOut = false;
  private currentUserId: number | null = null;
  private broadcastChannel: BroadcastChannel | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private actions$: Actions,
    private echoService: EchoService,
    private appTourService: AppTourService,
    private popupService: PopupService,
    private authService: AuthService
  ) {
    this.initCrossTabCommunication();
    this.trackCurrentUser();
    this.listenForLoginSuccess();
  }

  /**
   * Full sign out — calls backend API, clears state, broadcasts to other tabs
   */
  signOut(): void {
    if (this.isSigningOut) return;
    this.isSigningOut = true;

    // Dispatch logout action — triggers the logout effect which calls backend API
    this.store.dispatch(logout());

    // Listen to logoutSuccess action
    this.actions$.pipe(
      ofType(logoutSuccess),
      take(1)
    ).subscribe(() => {
      this.completeSignOut(true);
    });

    // Listen to logoutFailure action
    this.actions$.pipe(
      ofType(logoutFailure),
      take(1)
    ).subscribe((action) => {
      console.error('Logout failed:', action.error);
      this.popupService.notify('Logout failed, but you have been logged out locally', 'Logout', 'warning');
      this.completeSignOut(true);
    });
  }

  /**
   * Local-only sign out — used by other tabs when the originating tab already called the API
   */
  private localSignOut(): void {
    if (this.isSigningOut) return;
    this.isSigningOut = true;
    this.completeSignOut(false);
  }

  private completeSignOut(broadcast: boolean): void {
    // Reset entire app state (clears localStorage via rootReducer)
    this.store.dispatch(resetState());

    // Reset tour state
    this.appTourService.resetTourStateForUserChange();

    // Disconnect WebSocket
    this.echoService.disconnect();

    // Broadcast to other tabs
    if (broadcast) {
      this.broadcastMessage({ type: 'SIGN_OUT' });
    }

    // Hard redirect to ensure complete state reset
    window.location.href = '/login';
  }

  private initCrossTabCommunication(): void {
    // Primary: BroadcastChannel
    if (typeof BroadcastChannel !== 'undefined') {
      this.broadcastChannel = new BroadcastChannel('salesommo_auth');
      this.broadcastChannel.onmessage = (event: MessageEvent<CrossTabMessage>) => {
        this.handleCrossTabMessage(event.data);
      };
    }

    // Fallback: storage event for browsers without BroadcastChannel
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === 'salesommo_auth_event') {
        try {
          const message: CrossTabMessage = JSON.parse(event.newValue || '');
          this.handleCrossTabMessage(message);
        } catch {
          // ignore parse errors
        }
      }
    });
  }

  private handleCrossTabMessage(message: CrossTabMessage): void {
    if (message.type === 'SIGN_OUT') {
      this.localSignOut();
    } else if (message.type === 'SIGN_IN' && message.userId !== this.currentUserId) {
      // Different account signed in on another tab
      this.localSignOut();
    }
  }

  private broadcastMessage(message: CrossTabMessage): void {
    // BroadcastChannel
    if (this.broadcastChannel) {
      this.broadcastChannel.postMessage(message);
    }

    // Storage event fallback
    try {
      localStorage.setItem('salesommo_auth_event', JSON.stringify(message));
      // Remove immediately so the event fires again next time
      localStorage.removeItem('salesommo_auth_event');
    } catch {
      // ignore storage errors
    }
  }

  private trackCurrentUser(): void {
    this.authService.authUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUserId = user?.id ?? null;
      });
  }

  private listenForLoginSuccess(): void {
    this.actions$.pipe(
      ofType(loginSuccess),
      takeUntil(this.destroy$)
    ).subscribe(action => {
      const userId = action.user?.id;
      if (userId) {
        this.currentUserId = userId;
        this.broadcastMessage({ type: 'SIGN_IN', userId });
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
    }
  }
}
